import { useMemo, useState } from 'react';
import { MessageCircle, FileText } from 'lucide-react';
import type { ManagementTab } from './types/management';
import WhatsAppContactsTab from './components/WhatsAppContactsTab';
import EmailRecipientsTab from './components/EmailRecipientsTab';
import { PageTitle } from './../../../_metronic/layout/core';
import { GetPermissions } from './../../../app/modules/auth';

type TabConfig = {
  id: ManagementTab;
  label: string;
  icon: typeof MessageCircle;
  permissionKey: string; // substring que deve existir em feature.name
};

const tabs: TabConfig[] = [
  {
    id: 'whatsapp',
    label: 'Alertas - WhatsApp',
    icon: MessageCircle,
    permissionKey: 'settings-whatsapp_notification_numbers',
  },
  {
    id: 'emails',
    label: 'Pedidos sem Faturamento',
    icon: FileText,
    permissionKey: 'settings-emails_pedidos_sem_faturamento_magento',
  },
  {
    id: 'gaps-precificacao',
    label: 'Relatório - Gaps Precificação',
    icon: FileText,
    permissionKey: 'settings-relatorio-gaps-precificacao',
  },
  {
    id: 'maiores-descontos',
    label: 'Relatório - Maiores Descontos',
    icon: FileText,
    permissionKey: 'settings-relatorio-maiores-descontos',
  },
];

export default function Management() {
  const permissions = GetPermissions();
  const [activeTab, setActiveTab] = useState<ManagementTab>('whatsapp');

  const hasPermission = (permissionKey: string) =>
    !!permissions?.some((feature) => feature?.name?.includes(permissionKey));

  const visibleTabs = useMemo(
    () => tabs.filter((t) => hasPermission(t.permissionKey)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permissions]
  );

  // Garante que activeTab sempre aponta para uma aba permitida (ou nenhuma)
  const safeActiveTab = useMemo(() => {
    if (visibleTabs.some((t) => t.id === activeTab)) return activeTab;
    return visibleTabs[0]?.id ?? (activeTab as ManagementTab);
  }, [activeTab, visibleTabs]);

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <PageTitle>Configurações</PageTitle>

      <div className="mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex overflow-x-auto" aria-label="Tabs">
              {visibleTabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = safeActiveTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`relative flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? 'text-blue-600'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                    <span>{tab.label}</span>
                    {isActive && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />}
                  </button>
                );
              })}

              {visibleTabs.length === 0 && (
                <div className="px-6 py-4 text-sm text-gray-500">
                  Você não tem permissões para acessar estas configurações.
                </div>
              )}
            </nav>
          </div>
        </div>

        {safeActiveTab === 'whatsapp' && hasPermission('settings-whatsapp_notification_numbers') && (
          <WhatsAppContactsTab />
        )}

        {safeActiveTab === 'emails' && hasPermission('settings-emails_pedidos_sem_faturamento_magento') && (
          <EmailRecipientsTab permissionName="emails_pedidos_sem_faturamento_magento"/>
        )}

        {safeActiveTab === 'gaps-precificacao' && hasPermission('settings-relatorio-gaps-precificacao') && (
          <EmailRecipientsTab permissionName="settings-relatorio-gaps-precificacao"/>
        )}

        {safeActiveTab === 'maiores-descontos' && hasPermission('settings-relatorio-maiores-descontos') && (
          <EmailRecipientsTab permissionName="settings-relatorio-maiores-descontos"/>
        )}
      </div>
    </div>
  );
}
